import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Loader2, CreditCard, User, Phone, DollarSign, LogIn, ArrowRight, Shield, Globe, Minus, Plus } from 'lucide-react';
import axios from 'axios';
import { API, BACKEND_URL } from '../utils/api';
import { normalizeSwedishPhone, swedishMobileError } from '../utils/inputSanitizers';
import { useAuthContext } from '../context/AuthContext';
import { PAYMIG_LOGO_B64 } from '../utils/paymigLogo';

export default function GuestPaymentPage() {
  const { paymigId } = useParams();
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const authContext = useAuthContext();
  const token = authContext?.token;
  const user = authContext?.user;
  const headers = authContext?.headers;
  const { executeRecaptcha } = useGoogleReCaptcha?.() || {};

  const [loading, setLoading] = useState(true);
  const [subAccount, setSubAccount] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null); // null = choice, 'guest', 'loggedin'
  const [submitting, setSubmitting] = useState(false);

  // Guest form
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [message, setMessage] = useState('');
  const [quantities, setQuantities] = useState([]); // for LINE_ITEMS mode

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === 'sv' ? 'en' : 'sv');
  };

  // Auto-detect logged-in state (handles login redirect and cached sessions)
  useEffect(() => {
    if (token && user && subAccount) {
      setMode('loggedin');
    }
  }, [token, user, subAccount]);

  // Fetch sub-account info
  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await axios.get(`${API}/guest/pay-info/${paymigId}`);
        setSubAccount(res.data);
        if (res.data.fixed_amount) {
          setAmount(String(res.data.fixed_amount));
        }
        if (res.data.default_message) {
          setMessage(res.data.default_message);
        }
        // Init line item quantities
        if (res.data.payment_mode === 'LINE_ITEMS' && res.data.line_items) {
          setQuantities(res.data.line_items.map(() => 0));
        }
      } catch (e) {
        const status = e.response?.status;
        if (status === 404) {
          setError('invalidLink');
        } else if (status === 410) {
          setError('eventExpired');
        } else if (status === 403) {
          setError('unavailable');
        } else {
          setError('loadError');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [paymigId, token, user]);

  const isLineItems = subAccount?.payment_mode === 'LINE_ITEMS' && subAccount?.line_items?.length > 0;
  const lineItemsTotal = isLineItems ? subAccount.line_items.reduce((sum, li, i) => sum + li.amount * (quantities[i] || 0), 0) : 0;
  const hasSelection = isLineItems ? quantities.some(q => q > 0) : true;

  const updateQty = (idx, delta) => {
    setQuantities(prev => {
      const next = [...prev];
      next[idx] = Math.max(0, Math.min(99, (next[idx] || 0) + delta));
      return next;
    });
  };

  const requireDetails = subAccount?.require_guest_details !== false;
  const optionalSuffix = requireDetails ? '' : ` ${t('guestPayment.optionalSuffix')}`;

  const handleGuestPayment = async () => {
    const name = guestName.trim();
    const phone = guestPhone.trim();
    if (requireDetails && !name) return toast.error(t('guestPayment.enterValidName'));
    if ((requireDetails || phone) && swedishMobileError(phone)) return toast.error(swedishMobileError(phone));
    if (isLineItems && !hasSelection) return toast.error(t('guestPayment.itemsSelected'));
    if (!isLineItems && !subAccount.fixed_amount && (!amount || parseFloat(amount) <= 0)) return toast.error(t('guestPayment.enterValidAmount'));

    setSubmitting(true);
    try {
      let recaptchaToken = null;
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha('guest_payment');
      }

      const res = await axios.post(`${API}/guest/payment/create`, {
        paymig_id: paymigId,
        guest_name: name || undefined,
        guest_phone: phone ? normalizeSwedishPhone(phone) : undefined,
        amount: isLineItems ? lineItemsTotal : (subAccount.fixed_amount || parseFloat(amount)),
        message: message.trim() || undefined,
        recaptcha_token: recaptchaToken,
        origin_url: BACKEND_URL,
        line_items_selection: isLineItems ? quantities.map((q, i) => ({ index: i, quantity: q })).filter(s => s.quantity > 0) : undefined,
      });

      if (res.data.deeplink) {
        sessionStorage.setItem('guest_payment_id', res.data.payment_id);
        sessionStorage.setItem('guest_payment_nonce', res.data.callback_nonce);
        sessionStorage.setItem('guest_payment_amount', String(res.data.amount));
        sessionStorage.setItem('guest_payment_recipient', res.data.sub_account_name);
        window.location.href = res.data.deeplink;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t('guestPayment.failedToCreate'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleLoggedInPayment = async () => {
    if (isLineItems && !hasSelection) return toast.error(t('guestPayment.itemsSelected'));
    if (!isLineItems && !subAccount.fixed_amount && (!amount || parseFloat(amount) <= 0)) return toast.error(t('guestPayment.enterValidAmount'));

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/guest/payment/create-loggedin`, {
        paymig_id: paymigId,
        amount: isLineItems ? lineItemsTotal : (subAccount.fixed_amount || parseFloat(amount)),
        message: message.trim() || undefined,
        origin_url: BACKEND_URL,
        line_items_selection: isLineItems ? quantities.map((q, i) => ({ index: i, quantity: q })).filter(s => s.quantity > 0) : undefined,
      }, { headers });

      if (res.data.deeplink) {
        sessionStorage.setItem('pending_payment_id', res.data.payment_id);
        window.location.href = res.data.deeplink;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t('guestPayment.failedToCreate'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleLoginRedirect = () => {
    sessionStorage.setItem('post_login_redirect', window.location.pathname);
    nav('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="guest-pay-loading">
        <Loader2 className="h-10 w-10 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="guest-pay-error">
        <Card className="w-full max-w-md border-gray-200 shadow-lg">
          <CardContent className="pt-8 text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-50 flex items-center justify-center">
              <CreditCard className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900" data-testid={`guest-pay-error-${error}`}>{t(`guestPayment.${error}`)}</h2>
            <p className="text-sm text-gray-500">
              {t('guestPayment.contactSupport', { email: 'support@paymig.app' })}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-start p-4 pt-8" data-testid="guest-pay-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <img src={PAYMIG_LOGO_B64} alt="PayMig" className="w-10 h-10 rounded-lg" />
        <span className="text-xl font-bold text-gray-900 tracking-tight">PayMig</span>
        <button
          onClick={toggleLang}
          className="ml-3 flex items-center gap-1 px-2 py-1 rounded-md bg-gray-200 hover:bg-gray-300 text-xs text-gray-700 transition-colors"
          data-testid="lang-toggle"
        >
          <Globe className="h-3 w-3" />
          {i18n.language === 'sv' ? 'EN' : 'SV'}
        </button>
      </div>

      <Card className="w-full max-w-md border-gray-200 shadow-lg" data-testid="guest-pay-card">
        {/* Sub-account info */}
        <CardHeader className="pb-3">
          <div className="text-center space-y-1">
            {subAccount.business_logo_path && (
              <img
                src={`${API}/business/logo-file/${subAccount.business_logo_path}`}
                alt={subAccount.business_name || 'Business'}
                className="w-14 h-14 rounded-xl object-contain border border-gray-200 bg-white mx-auto mb-1"
                data-testid="guest-business-logo"
              />
            )}
            <p className="text-xs uppercase tracking-widest text-teal-600 font-semibold">
              {subAccount.type === 'EVENT' ? t('guestPayment.eventPayment') : t('guestPayment.payment')}
            </p>
            <CardTitle className="text-2xl text-gray-900">{subAccount.name}</CardTitle>
            {subAccount.business_name && (
              <p className="text-base text-gray-500">{subAccount.business_name}</p>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {subAccount.description && (
            <p className="text-base text-gray-600 text-center">{subAccount.description}</p>
          )}

          {/* Amount display or Line Items */}
          {isLineItems ? (
            <div className="space-y-2" data-testid="line-items-display">
              <p className="text-sm uppercase tracking-wider text-gray-500 font-medium text-center">{t('guestPayment.selectItems')}</p>
              {subAccount.line_items.map((li, idx) => (
                <div key={idx} className="flex items-center justify-between bg-white rounded-xl px-4 py-3 border border-gray-200 shadow-sm" data-testid={`line-item-row-${idx}`}>
                  <div>
                    <p className="text-base text-gray-900 font-medium">{li.description}</p>
                    <p className="text-sm text-gray-500">{li.amount} SEK</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQty(idx, -1)} disabled={!quantities[idx]} className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center disabled:opacity-30 transition-colors" data-testid={`qty-minus-${idx}`}>
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="text-gray-900 font-bold w-7 text-center text-lg" data-testid={`qty-value-${idx}`}>{quantities[idx] || 0}</span>
                    <button onClick={() => updateQty(idx, 1)} disabled={quantities[idx] >= 99} className="w-9 h-9 rounded-full bg-teal-600 hover:bg-teal-500 text-white flex items-center justify-center disabled:opacity-30 transition-colors" data-testid={`qty-plus-${idx}`}>
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              <div className="bg-teal-50 rounded-xl p-4 text-center border border-teal-200 mt-2" data-testid="line-items-total">
                <p className="text-sm text-teal-700 mb-1">{t('guestPayment.total')}</p>
                <p className="text-3xl font-bold text-gray-900">{lineItemsTotal.toFixed(0)} <span className="text-lg text-gray-500">SEK</span></p>
              </div>
            </div>
          ) : subAccount.fixed_amount ? (
            <div className="bg-teal-50 rounded-xl p-4 text-center border border-teal-200" data-testid="fixed-amount-display">
              <p className="text-sm text-teal-700 mb-1">{t('guestPayment.amount')}</p>
              <p className="text-3xl font-bold text-gray-900">{subAccount.fixed_amount} <span className="text-lg text-gray-500">SEK</span></p>
            </div>
          ) : null}

          <Separator className="bg-gray-200" />

          {/* Mode selection */}
          {mode === null && (
            <div className="space-y-3" data-testid="mode-selection">
              <Button
                onClick={() => setMode('guest')}
                className="w-full h-12 bg-teal-600 hover:bg-teal-500 text-white font-medium text-base"
                data-testid="continue-as-guest-btn"
              >
                <User className="h-5 w-5 mr-2" />
                {t('guestPayment.continueAsGuest')}
              </Button>

              <Button
                onClick={token ? () => setMode('loggedin') : handleLoginRedirect}
                variant="outline"
                className="w-full h-12 border-gray-300 text-gray-700 hover:bg-gray-100 font-medium text-base"
                data-testid="login-to-pay-btn"
              >
                <LogIn className="h-5 w-5 mr-2" />
                {token ? t('guestPayment.payWithAccount') : t('guestPayment.loginToPayMig')}
              </Button>
            </div>
          )}

          {/* Guest form */}
          {mode === 'guest' && (
            <div className="space-y-4" data-testid="guest-form">
              <div className="space-y-2">
                <Label className="text-gray-700 text-base font-medium" data-testid="guest-name-label">{t('guestPayment.yourName')}{optionalSuffix}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    value={guestName}
                    onChange={e => setGuestName(e.target.value)}
                    placeholder={t('guestPayment.enterFullName')}
                    className="pl-11 h-12 text-base border-gray-300 text-gray-900 placeholder:text-gray-400"
                    data-testid="guest-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700 text-base font-medium" data-testid="guest-phone-label">{t('guestPayment.phoneNumber')}{optionalSuffix}</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    value={guestPhone}
                    onChange={e => setGuestPhone(e.target.value)}
                    placeholder="+46 70 123 4567"
                    type="tel"
                    className="pl-11 h-12 text-base border-gray-300 text-gray-900 placeholder:text-gray-400"
                    data-testid="guest-phone-input"
                  />
                </div>
              </div>

              {!isLineItems && !subAccount.fixed_amount && (
                <div className="space-y-2">
                  <Label className="text-gray-700 text-base font-medium">{t('guestPayment.amountSek')}</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      value={amount}
                      onChange={e => setAmount(e.target.value)}
                      placeholder="0.00"
                      type="number"
                      min="1"
                      step="0.01"
                      className="pl-11 h-12 text-base border-gray-300 text-gray-900 placeholder:text-gray-400"
                      data-testid="guest-amount-input"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-gray-700 text-base font-medium">{t('guestPayment.messageOptional')}</Label>
                <Input
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder={t('guestPayment.addNote')}
                  maxLength={200}
                  className="h-12 text-base border-gray-300 text-gray-900 placeholder:text-gray-400"
                  data-testid="guest-message-input"
                />
              </div>

              <Button
                onClick={handleGuestPayment}
                disabled={submitting || (isLineItems && !hasSelection)}
                className="w-full h-12 bg-teal-600 hover:bg-teal-500 text-white font-semibold text-base"
                data-testid="guest-pay-submit-btn"
              >
                {submitting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    {t('guestPayment.payWithSwish', { amount: isLineItems ? lineItemsTotal.toFixed(0) : (subAccount.fixed_amount || amount || '...') })}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>

              <button
                onClick={() => setMode(null)}
                className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
                data-testid="guest-back-btn"
              >
                {t('guestPayment.back')}
              </button>
            </div>
          )}

          {/* Logged-in form */}
          {mode === 'loggedin' && token && user && (
            <div className="space-y-4" data-testid="loggedin-form">
              <div className="bg-teal-50 rounded-xl p-4 border border-teal-200">
                <p className="text-sm text-teal-700 mb-1">{t('guestPayment.payingAs')}</p>
                <p className="text-gray-900 font-medium text-base">{user.full_name}</p>
                <p className="text-sm text-gray-500">{user.swish_payee_alias || user.email}</p>
              </div>

              {!isLineItems && !subAccount.fixed_amount && (
                <div className="space-y-2">
                  <Label className="text-gray-700 text-base font-medium">{t('guestPayment.amountSek')}</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      value={amount}
                      onChange={e => setAmount(e.target.value)}
                      placeholder="0.00"
                      type="number"
                      min="1"
                      step="0.01"
                      className="pl-11 h-12 text-base border-gray-300 text-gray-900 placeholder:text-gray-400"
                      data-testid="loggedin-amount-input"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-gray-700 text-base font-medium">{t('guestPayment.messageOptional')}</Label>
                <Input
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder={t('guestPayment.addNote')}
                  maxLength={200}
                  className="h-12 text-base border-gray-300 text-gray-900 placeholder:text-gray-400"
                  data-testid="loggedin-message-input"
                />
              </div>

              <Button
                onClick={handleLoggedInPayment}
                disabled={submitting || (isLineItems && !hasSelection)}
                className="w-full h-12 bg-teal-600 hover:bg-teal-500 text-white font-semibold text-base"
                data-testid="loggedin-pay-submit-btn"
              >
                {submitting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    {t('guestPayment.payWithSwish', { amount: isLineItems ? lineItemsTotal.toFixed(0) : (subAccount.fixed_amount || amount || '...') })}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>

              <button
                onClick={() => setMode(null)}
                className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
                data-testid="loggedin-back-btn"
              >
                {t('guestPayment.back')}
              </button>
            </div>
          )}

          {/* Security note */}
          <div className="flex items-center gap-2 pt-2">
            <Shield className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            <p className="text-xs text-gray-500">
              {t('guestPayment.securedBySwish')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="mt-6 text-center space-y-1">
        <p className="text-xs text-gray-400">{t('guestPayment.poweredBy')}</p>
        <p className="text-xs text-gray-500">support@paymig.app</p>
      </div>
    </div>
  );
}
